import { useState, useCallback } from 'react';
import {
  Table,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Typography,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  useWisecureQuery,
  useWisecureMutation,
  useInvalidateQueries,
} from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import type { PaginatedResponse } from '@/types/dashboard';
import type {
  SecurityDomainResponse,
  CreateSecurityDomainDto,
  UpdateSecurityDomainDto,
} from '@wisecure/api-client';

const { Text } = Typography;

export function SecurityDomainsPage() {
  const invalidate = useInvalidateQueries();
  const [search, setSearch] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<SecurityDomainResponse | null>(null);
  const [form] = Form.useForm();

  const { data: domainsResponse, isLoading } = useWisecureQuery<
    PaginatedResponse<SecurityDomainResponse>
  >(
    queryKeys.securityDomains,
    (client) =>
      client.securityDomains.securityDomainsControllerFindAll({
        limit: 100,
        ...(search ? { search } : {}),
      }) as Promise<PaginatedResponse<SecurityDomainResponse>>,
  );

  const createMutation = useWisecureMutation(
    (client, values: CreateSecurityDomainDto) =>
      client.securityDomains.securityDomainsControllerCreate({ requestBody: values }),
    {
      onSuccess: () => {
        message.success('Domaine de sécurité créé');
        invalidate(queryKeys.securityDomains);
        handleClose();
      },
      onError: (error: { body?: { message?: string }; message?: string }) => {
        const msg = error?.body?.message ?? error?.message;
        if (msg?.includes('already exists')) {
          message.error('Un domaine avec ce label français existe déjà');
        } else {
          message.error('Erreur lors de la création du domaine');
        }
      },
    },
  );

  const updateMutation = useWisecureMutation(
    (client, { id, values }: { id: string; values: UpdateSecurityDomainDto }) =>
      client.securityDomains.securityDomainsControllerUpdate({ id, requestBody: values }),
    {
      onSuccess: () => {
        message.success('Domaine de sécurité mis à jour');
        invalidate(queryKeys.securityDomains);
        handleClose();
      },
      onError: () => {
        message.error('Erreur lors de la mise à jour du domaine');
      },
    },
  );

  const toggleEnabledMutation = useWisecureMutation(
    (client, { id, enabled }: { id: string; enabled: boolean }) =>
      client.securityDomains.securityDomainsControllerUpdate({
        id,
        requestBody: { enabled },
      }),
    {
      onSuccess: () => {
        message.success('Statut mis à jour');
        invalidate(queryKeys.securityDomains);
      },
      onError: () => {
        message.error('Erreur lors de la mise à jour du statut');
      },
    },
  );

  const deleteMutation = useWisecureMutation<unknown, string>(
    (client, id) => client.securityDomains.securityDomainsControllerRemove({ id }),
    {
      onSuccess: () => {
        message.success('Domaine de sécurité supprimé');
        invalidate(queryKeys.securityDomains);
      },
      onError: () => {
        message.error('Erreur lors de la suppression');
      },
    },
  );

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setEditingDomain(null);
    form.resetFields();
  }, [form]);

  const handleOpenCreate = useCallback(() => {
    setEditingDomain(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setModalOpen(true);
  }, [form]);

  const handleOpenEdit = useCallback(
    (domain: SecurityDomainResponse) => {
      setEditingDomain(domain);
      form.setFieldsValue({
        frenchLabel: domain.frenchLabel,
        englishLabel: domain.englishLabel,
        enabled: domain.enabled,
      });
      setModalOpen(true);
    },
    [form],
  );

  const handleSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      if (editingDomain) {
        updateMutation.mutate({ id: editingDomain.id, values });
      } else {
        createMutation.mutate(values as CreateSecurityDomainDto);
      }
    });
  }, [form, editingDomain, createMutation, updateMutation]);

  const columns = [
    {
      title: 'Label français',
      dataIndex: 'frenchLabel',
      key: 'frenchLabel',
      render: (label: string) => (
        <Text strong style={{ fontSize: 14 }}>
          {label}
        </Text>
      ),
    },
    {
      title: 'Label anglais',
      dataIndex: 'englishLabel',
      key: 'englishLabel',
      render: (label: string) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Text>
      ),
    },
    {
      title: 'Activé',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled: boolean, record: SecurityDomainResponse) => (
        <Switch
          size="small"
          checked={enabled}
          loading={toggleEnabledMutation.isPending}
          onChange={(checked) =>
            toggleEnabledMutation.mutate({ id: record.id, enabled: checked })
          }
        />
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: SecurityDomainResponse) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEdit(record)}
          />
          <Popconfirm
            title="Supprimer le domaine ?"
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const data = (domainsResponse?.data ?? []) as SecurityDomainResponse[];
  const total = domainsResponse?.total ?? 0;

  return (
    <div>
      <PageHeader
        title="Domaines de sécurité"
        subtitle={`${total} domaine(s) enregistré(s)`}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Ajouter un domaine
          </Button>
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="Rechercher par label..."
          allowClear
          onSearch={(value) => setSearch(value)}
          style={{ width: 250 }}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        rowKey="id"
        pagination={false}
      />

      <Modal
        open={modalOpen}
        onCancel={handleClose}
        title={editingDomain ? 'Modifier le domaine' : 'Créer un domaine de sécurité'}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ enabled: true }}
        >
          <Form.Item
            name="frenchLabel"
            label="Label français"
            rules={[{ required: true, message: 'Le label français est requis' }]}
          >
            <Input placeholder="Ex: Alarme intrusion" />
          </Form.Item>

          <Form.Item
            name="englishLabel"
            label="Label anglais"
            rules={[{ required: true, message: 'Le label anglais est requis' }]}
          >
            <Input placeholder="Ex: Intrusion alarm" />
          </Form.Item>

          <Form.Item
            name="enabled"
            label="Activé"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
